import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  DataCollectionForm,
  DataCollectionFormStatus,
  FormCollector,
  RedirectFormStatusValue,
} from '../types/data-collection-form.types';
import { buildFormPublicUrl } from './build-form-public-url';
import { parseDcfTagString } from './dcf-tag-utils';

export function getDataCollectionFormRowKey(row: DataCollectionForm): string {
  return row.id;
}

export function getDataCollectionFormStatusVariant(
  status: DataCollectionFormStatus,
): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'active':
      return 'success';
    case 'draft':
      return 'warning';
    default:
      return 'default';
  }
}

export function getQrCodeImageUrl(url: string, size = 280): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${String(size)}x${String(size)}&data=${encodeURIComponent(url)}`;
}

function mapFormStatus(formStatus: string): DataCollectionFormStatus {
  return formStatus === 'ATTIVO' ? 'active' : 'inactive';
}

export function isCancelledFormStatus(
  formStatus: string | null | undefined,
): boolean {
  return (formStatus ?? '').trim().toUpperCase() === 'CANCELLATO';
}

export function toApiFormStatus(active: boolean): RedirectFormStatusValue {
  return active ? 'ATTIVO' : 'SOSPESO';
}

export function normalizeFormCollector(row: FormCollector): DataCollectionForm {
  return {
    id: row.formCode,
    formCode: row.formCode,
    formName: row.formName ?? row.formCode,
    status: mapFormStatus(row.formStatus ?? ''),
    interactions: row.interactionCounter,
    url: buildFormPublicUrl(row.formCode),
    tags: parseDcfTagString(row.tagsApplied),
  };
}

export function normalizeFormCollectorItem(
  value: unknown,
): DataCollectionForm | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const row = value as Partial<FormCollector>;
  const formCode = typeof row.formCode === 'string' ? row.formCode.trim() : '';
  if (!formCode) {
    return null;
  }

  const formStatus = typeof row.formStatus === 'string' ? row.formStatus : '';
  if (isCancelledFormStatus(formStatus)) {
    return null;
  }

  return normalizeFormCollector({
    ...(row as FormCollector),
    formCode,
    formName: typeof row.formName === 'string' ? row.formName : formCode,
    formStatus,
    interactionCounter:
      typeof row.interactionCounter === 'number' ? row.interactionCounter : 0,
  });
}

export function normalizeFormCollectorList(
  rows: FormCollector[],
): DataCollectionForm[] {
  return rows
    .map(normalizeFormCollectorItem)
    .filter((row): row is DataCollectionForm => row !== null);
}

export function filterDataCollectionFormsBySearch(
  rows: DataCollectionForm[],
  search: string,
): DataCollectionForm[] {
  const query = search.trim().toLowerCase();
  if (!query) {
    return rows;
  }

  return rows.filter(
    (form) =>
      form.formName.toLowerCase().includes(query) ||
      form.formCode.toLowerCase().includes(query),
  );
}

function readFiniteNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

/**
 * Supports both:
 * - Spring page: `{ content, totalElements, totalPages, number, size, pageable }`
 * - Legacy bare array (client-side page/size applied)
 */
export function normalizeFormCollectorsPageResponse(
  results: unknown,
  options: { page?: number; size?: number },
): PaginatedResponse<DataCollectionForm> {
  const requestedPage = Math.max(0, options.page ?? 0);
  const requestedSize = Math.max(1, options.size ?? 10);

  if (
    results &&
    typeof results === 'object' &&
    Array.isArray((results as { content?: unknown }).content)
  ) {
    const page = results as {
      content: unknown[];
      totalElements?: unknown;
      totalPages?: unknown;
      number?: unknown;
      size?: unknown;
      pageable?: {
        pageNumber?: unknown;
        pageSize?: unknown;
      };
    };

    const rows = page.content
      .map(normalizeFormCollectorItem)
      .filter((row): row is DataCollectionForm => row !== null);

    const pageNumber = readFiniteNumber(
      page.number ?? page.pageable?.pageNumber,
      requestedPage,
    );
    const pageSize = readFiniteNumber(
      page.size ?? page.pageable?.pageSize,
      requestedSize,
    );
    const totalElements = readFiniteNumber(page.totalElements, rows.length);
    const totalPages = Math.max(
      1,
      readFiniteNumber(
        page.totalPages,
        Math.ceil(totalElements / pageSize) || 1,
      ),
    );

    return {
      data: rows,
      meta: {
        page: pageNumber + 1,
        limit: pageSize,
        total: totalElements,
        totalPages,
      },
    };
  }

  const allRows = Array.isArray(results)
    ? results
        .map(normalizeFormCollectorItem)
        .filter((row): row is DataCollectionForm => row !== null)
    : (() => {
        const single = normalizeFormCollectorItem(results);
        return single ? [single] : [];
      })();

  const total = allRows.length;
  const start = requestedPage * requestedSize;

  return {
    data: allRows.slice(start, start + requestedSize),
    meta: {
      page: requestedPage + 1,
      limit: requestedSize,
      total,
      totalPages: Math.max(1, Math.ceil(total / requestedSize) || 1),
    },
  };
}
